/**
 * Seed-data utility for dev / demo tenants.
 *
 * Builds a small but representative master-data seed using the same
 * generated services every page goes through. Run from the browser
 * (e.g. inside the API browser's "Seed sample data" button) — every
 * row goes through the live `apiClient` so auth, tenant scoping and
 * the per-row audit log all behave exactly like a real user create.
 */
import { apiClient } from './client';

export interface SeedResult {
  resource: string;
  attempted: number;
  created: number;
  failed: number;
  errors: string[];
}

export interface SeedSummary {
  results: SeedResult[];
  totalCreated: number;
  totalFailed: number;
}

interface SeedSpec {
  /** Absolute API path, e.g. `/master-data/Bank`. */
  path: string;
  /** Display name used in the result. */
  resource: string;
  /** Rows to POST one-by-one. */
  rows: Record<string, unknown>[];
}

/* -------------------------------------------------------------------- */
/*  Sample rows                                                         */
/* -------------------------------------------------------------------- */

const COUNTRIES: Record<string, unknown>[] = [
  { code: 'IN', name: 'India',          isActive: 1 },
  { code: 'US', name: 'United States',  isActive: 1 },
  { code: 'GB', name: 'United Kingdom', isActive: 1 },
];

const CURRENCIES: Record<string, unknown>[] = [
  { code: 'INR', name: 'Indian Rupee', isActive: 1 },
  { code: 'USD', name: 'US Dollar',    isActive: 1 },
  { code: 'EUR', name: 'Euro',         isActive: 1 },
];

const UNITS: Record<string, unknown>[] = [
  { code: 'PCS', name: 'Pieces', isActive: 1 },
  { code: 'KG',  name: 'Kilogram', isActive: 1 },
  { code: 'BOX', name: 'Box',    isActive: 1 },
  { code: 'LTR', name: 'Litre',  isActive: 1 },
];

const BRANDS: Record<string, unknown>[] = [
  { code: 'BR-AMUL', name: 'Amul',  isActive: 1 },
  { code: 'BR-NEST', name: 'Nestlé', isActive: 1 },
  { code: 'BR-TATA', name: 'Tata',  isActive: 1 },
];

const CATEGORIES: Record<string, unknown>[] = [
  { code: 'CAT-DAIRY',  name: 'Dairy products',     isActive: 1 },
  { code: 'CAT-SNACKS', name: 'Snacks & beverages', isActive: 1 },
  { code: 'CAT-PERSON', name: 'Personal care',      isActive: 1 },
];

const BANKS: Record<string, unknown>[] = [
  { code: 'SBIN',  name: 'State Bank of India', isActive: 1 },
  { code: 'HDFC',  name: 'HDFC Bank',           isActive: 1 },
  { code: 'ICICI', name: 'ICICI Bank',          isActive: 1 },
];

const DEPARTMENTS: Record<string, unknown>[] = [
  { code: 'D-OPS',  name: 'Operations', isActive: 1 },
  { code: 'D-FIN',  name: 'Finance',    isActive: 1 },
  { code: 'D-SALE', name: 'Sales',      isActive: 1 },
];

const DESIGNATIONS: Record<string, unknown>[] = [
  { code: 'DG-MGR',  name: 'Manager',    isActive: 1 },
  { code: 'DG-EXEC', name: 'Executive',  isActive: 1 },
  { code: 'DG-CASH', name: 'Cashier',    isActive: 1 },
];

const TAX_CLASSES: Record<string, unknown>[] = [
  { code: 'GST-5',  name: 'GST 5%',  isActive: 1 },
  { code: 'GST-12', name: 'GST 12%', isActive: 1 },
  { code: 'GST-18', name: 'GST 18%', isActive: 1 },
  { code: 'GST-28', name: 'GST 28%', isActive: 1 },
];

const SAMPLE_SEEDS: SeedSpec[] = [
  { path: '/master-data/Country',    resource: 'Country',     rows: COUNTRIES },
  { path: '/master-data/Currency',   resource: 'Currency',    rows: CURRENCIES },
  { path: '/master-data/UnitOfMeasure', resource: 'UnitOfMeasure', rows: UNITS },
  { path: '/master-data/ProductBrand',  resource: 'ProductBrand',  rows: BRANDS },
  { path: '/master-data/ProductCategory', resource: 'ProductCategory', rows: CATEGORIES },
  { path: '/master-data/Bank',       resource: 'Bank',        rows: BANKS },
  { path: '/master-data/Department', resource: 'Department',  rows: DEPARTMENTS },
  { path: '/master-data/Designation', resource: 'Designation', rows: DESIGNATIONS },
  { path: '/master-data/TaxClass',   resource: 'TaxClass',    rows: TAX_CLASSES },
];

/* -------------------------------------------------------------------- */
/*  Seeder                                                              */
/* -------------------------------------------------------------------- */

/**
 * Seed master-data rows into the active tenant. Idempotent on the
 * `code` field — skipped silently if the row already exists.
 */
export async function seedSampleData(): Promise<SeedSummary> {
  const results: SeedResult[] = [];
  let totalCreated = 0;
  let totalFailed = 0;

  for (const spec of SAMPLE_SEEDS) {
    const result: SeedResult = {
      resource: spec.resource,
      attempted: spec.rows.length,
      created: 0,
      failed: 0,
      errors: [],
    };

    for (const row of spec.rows) {
      try {
        await apiClient.post(spec.path, row);
        result.created++;
        totalCreated++;
      } catch (e) {
        result.failed++;
        totalFailed++;
        const msg = e instanceof Error ? e.message : 'failed';
        result.errors.push(`${row.code ?? row.name ?? '?'}: ${msg}`);
      }
    }

    results.push(result);
  }

  return { results, totalCreated, totalFailed };
}

export const SAMPLE_SEED_RESOURCES = SAMPLE_SEEDS.map((s) => s.resource);

/* -------------------------------------------------------------------- */
/*  BillBook menu cleanup + seed                                        */
/* -------------------------------------------------------------------- */

/**
 * One module-wise menu entry the BillBook sidebar / live `Menu` table
 * should expose. Mirrors the static `sidebarNav.json` so the live
 * `useDynamicMenu` hook lights up with the same tree once the backend
 * Menu resource is wired.
 *
 * Path is for the panel route; the backend may also store a fully-qualified
 * URL in `url` — we send both so the API is free to choose.
 */
interface MenuSeedRow {
  code: string;
  name: string;
  icon?: string;
  parentCode?: string;
  path?: string;
  sortOrder: number;
  isActive: number;
}

/** BillBook module-wise menu seed. Top-level groups + a curated set of
 *  primary children per module — the same shape the static sidebar ships. */
const BILLBOOK_MENU_ROWS: MenuSeedRow[] = [
  // Top-level groups
  { code: 'M-DASH',  name: 'Dashboard',           icon: 'LayoutDashboard', sortOrder: 10,  isActive: 1, path: '/panel/dashboard' },
  { code: 'M-SALES', name: 'Sales & Billing',     icon: 'ReceiptText',     sortOrder: 20,  isActive: 1 },
  { code: 'M-PURCH', name: 'Purchase',            icon: 'Truck',           sortOrder: 30,  isActive: 1 },
  { code: 'M-INV',   name: 'Inventory',           icon: 'Warehouse',       sortOrder: 40,  isActive: 1 },
  { code: 'M-POS',   name: 'POS',                 icon: 'Store',           sortOrder: 50,  isActive: 1 },
  { code: 'M-REST',  name: 'Restaurant',          icon: 'UtensilsCrossed', sortOrder: 60,  isActive: 1 },
  { code: 'M-ACC',   name: 'Accounting',          icon: 'Calculator',      sortOrder: 70,  isActive: 1 },
  { code: 'M-CRM',   name: 'CRM',                 icon: 'HeartHandshake',  sortOrder: 80,  isActive: 1 },
  { code: 'M-HRM',   name: 'HRM & Payroll',       icon: 'Users',           sortOrder: 90,  isActive: 1 },
  { code: 'M-MFG',   name: 'Manufacturing',       icon: 'Factory',         sortOrder: 100, isActive: 1 },
  { code: 'M-ASSET', name: 'Asset Management',    icon: 'Building',        sortOrder: 110, isActive: 1 },
  { code: 'M-MD',    name: 'Master Data',         icon: 'Database',        sortOrder: 120, isActive: 1 },
  { code: 'M-RPT',   name: 'Reports',             icon: 'BarChart3',       sortOrder: 130, isActive: 1 },
  { code: 'M-AI',    name: 'AI & ML',             icon: 'Sparkles',        sortOrder: 140, isActive: 1 },
  { code: 'M-WF',    name: 'Workflow',            icon: 'GitBranch',       sortOrder: 150, isActive: 1 },
  { code: 'M-DOC',   name: 'Documents',           icon: 'FileText',        sortOrder: 160, isActive: 1 },
  { code: 'M-NOTIF', name: 'Notifications',       icon: 'Bell',            sortOrder: 170, isActive: 1 },
  { code: 'M-AUDIT', name: 'Audit & Compliance',  icon: 'ShieldCheck',     sortOrder: 180, isActive: 1 },
  { code: 'M-TENT',  name: 'Tenancy & Billing',   icon: 'Building2',       sortOrder: 190, isActive: 1 },
  { code: 'M-INTG',  name: 'Integrations',        icon: 'Plug',            sortOrder: 200, isActive: 1 },
  { code: 'M-ADMIN', name: 'Administration',      icon: 'Settings',        sortOrder: 210, isActive: 1 },
  { code: 'M-API',   name: 'API Browser',         icon: 'Database',        sortOrder: 220, isActive: 1, path: '/panel/api' },

  // Sales children
  { code: 'M-SALES-INV',    name: 'Sales Invoices',     parentCode: 'M-SALES', sortOrder: 1, isActive: 1, path: '/panel/api/sales/SalesInvoice' },
  { code: 'M-SALES-ORD',    name: 'Sales Orders',       parentCode: 'M-SALES', sortOrder: 2, isActive: 1, path: '/panel/api/sales/SalesOrder' },
  { code: 'M-SALES-PAY',    name: 'Sales Payments',     parentCode: 'M-SALES', sortOrder: 3, isActive: 1, path: '/panel/api/sales/SalesPayment' },
  { code: 'M-SALES-RET',    name: 'Sales Returns',      parentCode: 'M-SALES', sortOrder: 4, isActive: 1, path: '/panel/api/sales/SalesReturn' },
  { code: 'M-SALES-CN',     name: 'Credit Notes',       parentCode: 'M-SALES', sortOrder: 5, isActive: 1, path: '/panel/api/sales/CreditNote' },
  { code: 'M-SALES-DN',     name: 'Debit Notes',        parentCode: 'M-SALES', sortOrder: 6, isActive: 1, path: '/panel/api/sales/DebitNote' },
  { code: 'M-SALES-DC',     name: 'Delivery Challans',  parentCode: 'M-SALES', sortOrder: 7, isActive: 1, path: '/panel/api/sales/DeliveryChallan' },
  { code: 'M-SALES-COUPON', name: 'Coupons',            parentCode: 'M-SALES', sortOrder: 8, isActive: 1, path: '/panel/api/sales/Coupon' },
  { code: 'M-SALES-PROMO',  name: 'Promotions',         parentCode: 'M-SALES', sortOrder: 9, isActive: 1, path: '/panel/api/sales/Promotion' },

  // Purchase children
  { code: 'M-PURCH-PO',  name: 'Purchase Orders',   parentCode: 'M-PURCH', sortOrder: 1, isActive: 1, path: '/panel/api/purchase/PurchaseOrder' },
  { code: 'M-PURCH-INV', name: 'Purchase Invoices', parentCode: 'M-PURCH', sortOrder: 2, isActive: 1, path: '/panel/api/purchase/PurchaseInvoice' },
  { code: 'M-PURCH-PAY', name: 'Purchase Payments', parentCode: 'M-PURCH', sortOrder: 3, isActive: 1, path: '/panel/api/purchase/PurchasePayment' },
  { code: 'M-PURCH-RET', name: 'Purchase Returns',  parentCode: 'M-PURCH', sortOrder: 4, isActive: 1, path: '/panel/api/purchase/PurchaseReturn' },
  { code: 'M-PURCH-GRN', name: 'Goods Receipt',     parentCode: 'M-PURCH', sortOrder: 5, isActive: 1, path: '/panel/api/purchase/GoodsReceiptNote' },
  { code: 'M-PURCH-SUP', name: 'Suppliers',         parentCode: 'M-PURCH', sortOrder: 6, isActive: 1, path: '/panel/api/purchase/Supplier' },

  // Inventory children
  { code: 'M-INV-BAL',  name: 'Stock Balance',     parentCode: 'M-INV', sortOrder: 1, isActive: 1, path: '/panel/api/purchase/StockBalance' },
  { code: 'M-INV-MOV',  name: 'Stock Movements',   parentCode: 'M-INV', sortOrder: 2, isActive: 1, path: '/panel/api/purchase/InventoryMovement' },
  { code: 'M-INV-ADJ',  name: 'Stock Adjustments', parentCode: 'M-INV', sortOrder: 3, isActive: 1, path: '/panel/api/purchase/StockAdjustment' },
  { code: 'M-INV-TRF',  name: 'Stock Transfers',   parentCode: 'M-INV', sortOrder: 4, isActive: 1, path: '/panel/api/purchase/StockTransfer' },
  { code: 'M-INV-WH',   name: 'Warehouses',        parentCode: 'M-INV', sortOrder: 5, isActive: 1, path: '/panel/api/master-data/Warehouse' },

  // POS children
  { code: 'M-POS-TERM',  name: 'Terminals',          parentCode: 'M-POS', sortOrder: 1, isActive: 1, path: '/panel/api/restaurant/PosTerminal' },
  { code: 'M-POS-SHIFT', name: 'Shifts',             parentCode: 'M-POS', sortOrder: 2, isActive: 1, path: '/panel/api/restaurant/PosShift' },
  { code: 'M-POS-CASH',  name: 'Cash Sessions',      parentCode: 'M-POS', sortOrder: 3, isActive: 1, path: '/panel/api/restaurant/CashDrawerSession' },

  // Restaurant children — enriched 2026-05-29 for the Jaipur Backyard demo.
  // Covers the full operator+customer cycle: take orders → assign waiter →
  // print KOT → serve → bill → generate QR.
  { code: 'M-REST-FLOOR', name: 'Floor & Tables',     parentCode: 'M-REST', sortOrder: 1, isActive: 1, path: '/panel/restaurant/floor' },
  { code: 'M-REST-ORD',   name: 'Orders',             parentCode: 'M-REST', sortOrder: 2, isActive: 1, path: '/panel/api/restaurant/RestaurantOrder' },
  { code: 'M-REST-KOT',   name: 'KOT (Kitchen Tickets)', parentCode: 'M-REST', sortOrder: 3, isActive: 1, path: '/panel/api/restaurant/Kot' },
  { code: 'M-REST-KDS',   name: 'Kitchen Display',    parentCode: 'M-REST', sortOrder: 4, isActive: 1, path: '/panel/restaurant/kitchen' },
  { code: 'M-REST-WAIT',  name: 'Waiter Queue',       parentCode: 'M-REST', sortOrder: 5, isActive: 1, path: '/panel/restaurant/waiter' },
  { code: 'M-REST-MNU-CAT', name: 'Menu Categories',  parentCode: 'M-REST', sortOrder: 6, isActive: 1, path: '/panel/api/restaurant/MenuCategory' },
  { code: 'M-REST-MNU-ITM', name: 'Menu Items',       parentCode: 'M-REST', sortOrder: 7, isActive: 1, path: '/panel/api/restaurant/MenuItem' },
  { code: 'M-REST-MNU-MOD', name: 'Menu Modifiers',   parentCode: 'M-REST', sortOrder: 8, isActive: 1, path: '/panel/api/restaurant/MenuItemModifier' },
  { code: 'M-REST-TBL',   name: 'Restaurant Tables',  parentCode: 'M-REST', sortOrder: 9, isActive: 1, path: '/panel/api/restaurant/RestaurantTable' },
  { code: 'M-REST-QR',    name: 'Table QR Codes',     parentCode: 'M-REST', sortOrder: 10, isActive: 1, path: '/panel/restaurant/qr-codes' },
  { code: 'M-REST-RSV',   name: 'Reservations',       parentCode: 'M-REST', sortOrder: 11, isActive: 1, path: '/panel/api/restaurant/Reservation' },
  { code: 'M-REST-INGD',  name: 'Ingredients & Recipes', parentCode: 'M-REST', sortOrder: 12, isActive: 1, path: '/panel/api/restaurant/Recipe' },

  // Accounting children
  { code: 'M-ACC-COA',  name: 'Chart of Accounts',  parentCode: 'M-ACC', sortOrder: 1, isActive: 1, path: '/panel/api/accounting/ChartOfAccount' },
  { code: 'M-ACC-JE',   name: 'Journal Entries',    parentCode: 'M-ACC', sortOrder: 2, isActive: 1, path: '/panel/api/accounting/JournalEntry' },
  { code: 'M-ACC-LDG',  name: 'Ledger',             parentCode: 'M-ACC', sortOrder: 3, isActive: 1, path: '/panel/api/accounting/LedgerBalance' },
  { code: 'M-ACC-BANK', name: 'Bank Accounts',      parentCode: 'M-ACC', sortOrder: 4, isActive: 1, path: '/panel/api/accounting/BankAccount' },
  { code: 'M-ACC-TAX',  name: 'Tax Returns',        parentCode: 'M-ACC', sortOrder: 5, isActive: 1, path: '/panel/api/accounting/TaxReturn' },
  { code: 'M-ACC-EINV', name: 'e-Invoice',          parentCode: 'M-ACC', sortOrder: 6, isActive: 1, path: '/panel/api/accounting/EInvoiceRecord' },

  // CRM children
  { code: 'M-CRM-CUST',  name: 'Customers',         parentCode: 'M-CRM', sortOrder: 1, isActive: 1, path: '/panel/api/crm/Customer' },
  { code: 'M-CRM-LEAD',  name: 'Leads',             parentCode: 'M-CRM', sortOrder: 2, isActive: 1, path: '/panel/api/crm/Lead' },
  { code: 'M-CRM-OPP',   name: 'Opportunities',     parentCode: 'M-CRM', sortOrder: 3, isActive: 1, path: '/panel/api/crm/Opportunity' },
  { code: 'M-CRM-QUO',   name: 'Quotations',        parentCode: 'M-CRM', sortOrder: 4, isActive: 1, path: '/panel/api/crm/Quotation' },

  // HRM children
  { code: 'M-HRM-EMP',  name: 'Employees',          parentCode: 'M-HRM', sortOrder: 1, isActive: 1, path: '/panel/employees' },
  { code: 'M-HRM-ATT',  name: 'Attendance',         parentCode: 'M-HRM', sortOrder: 2, isActive: 1, path: '/panel/api/hrm/Attendance' },
  { code: 'M-HRM-LV',   name: 'Leaves',             parentCode: 'M-HRM', sortOrder: 3, isActive: 1, path: '/panel/api/hrm/LeaveApplication' },
  { code: 'M-HRM-PAY',  name: 'Payroll Runs',       parentCode: 'M-HRM', sortOrder: 4, isActive: 1, path: '/panel/api/hrm/PayrollRun' },
  { code: 'M-HRM-PSL',  name: 'Payslips',           parentCode: 'M-HRM', sortOrder: 5, isActive: 1, path: '/panel/api/hrm/Payslip' },

  // Master Data children
  { code: 'M-MD-PROD',  name: 'Products',           parentCode: 'M-MD', sortOrder: 1, isActive: 1, path: '/panel/api/master-data/Product' },
  { code: 'M-MD-CAT',   name: 'Categories',         parentCode: 'M-MD', sortOrder: 2, isActive: 1, path: '/panel/api/master-data/ProductCategory' },
  { code: 'M-MD-BRD',   name: 'Brands',             parentCode: 'M-MD', sortOrder: 3, isActive: 1, path: '/panel/api/master-data/ProductBrand' },
  { code: 'M-MD-UOM',   name: 'Units',              parentCode: 'M-MD', sortOrder: 4, isActive: 1, path: '/panel/api/master-data/UnitOfMeasure' },
  { code: 'M-MD-CUR',   name: 'Currencies',         parentCode: 'M-MD', sortOrder: 5, isActive: 1, path: '/panel/api/master-data/Currency' },
  { code: 'M-MD-BANK',  name: 'Banks',              parentCode: 'M-MD', sortOrder: 6, isActive: 1, path: '/panel/api/master-data/Bank' },
  { code: 'M-MD-DEPT',  name: 'Departments',        parentCode: 'M-MD', sortOrder: 7, isActive: 1, path: '/panel/api/master-data/Department' },
  { code: 'M-MD-DESG',  name: 'Designations',       parentCode: 'M-MD', sortOrder: 8, isActive: 1, path: '/panel/api/master-data/Designation' },

  // Tenancy children
  { code: 'M-TENT-T',   name: 'Tenants',            parentCode: 'M-TENT', sortOrder: 1, isActive: 1, path: '/panel/api/tenancy/Tenant' },
  { code: 'M-TENT-BR',  name: 'Branches',           parentCode: 'M-TENT', sortOrder: 2, isActive: 1, path: '/panel/api/tenancy/Branch' },
  { code: 'M-TENT-SUB', name: 'Subscriptions',      parentCode: 'M-TENT', sortOrder: 3, isActive: 1, path: '/panel/api/tenancy/TenantSubscription' },
  { code: 'M-TENT-PLN', name: 'Subscription Plans', parentCode: 'M-TENT', sortOrder: 4, isActive: 1, path: '/panel/api/tenancy/SubscriptionPlan' },

  // Administration children
  { code: 'M-ADMIN-USR', name: 'Users',             parentCode: 'M-ADMIN', sortOrder: 1, isActive: 1, path: '/panel/api/identity/User' },
  { code: 'M-ADMIN-ROL', name: 'Roles',             parentCode: 'M-ADMIN', sortOrder: 2, isActive: 1, path: '/panel/api/authorization/Role' },
  { code: 'M-ADMIN-PRM', name: 'Permissions',       parentCode: 'M-ADMIN', sortOrder: 3, isActive: 1, path: '/panel/api/authorization/Permission' },
  { code: 'M-ADMIN-FF',  name: 'Feature Flags',     parentCode: 'M-ADMIN', sortOrder: 4, isActive: 1, path: '/panel/api/platform/FeatureFlag' },
  { code: 'M-ADMIN-SET', name: 'System Settings',   parentCode: 'M-ADMIN', sortOrder: 5, isActive: 1, path: '/panel/api/platform/SystemSetting' },
];

const MENU_BASE_PATH = '/restaurant/Menu';

/** Best-effort delete of every existing menu row. Returns count attempted
 *  + count actually deleted. Failures are tolerated — the seed step that
 *  follows is idempotent on `code`. */
export async function deleteAllLiveMenus(): Promise<{ attempted: number; deleted: number; errors: string[] }> {
  const errors: string[] = [];
  let attempted = 0;
  let deleted = 0;
  try {
    const r = await apiClient.get(MENU_BASE_PATH);
    const data = r.data;
    let list: Record<string, unknown>[] = [];
    if (Array.isArray(data)) list = data as Record<string, unknown>[];
    else if (data && typeof data === 'object') {
      const obj = data as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } };
      if (Array.isArray(obj.items)) list = obj.items as Record<string, unknown>[];
      else if (Array.isArray(obj.data)) list = obj.data as Record<string, unknown>[];
      else if (obj.data && Array.isArray((obj.data as { items?: unknown[] }).items)) {
        list = (obj.data as { items: unknown[] }).items as Record<string, unknown>[];
      }
    }
    for (const row of list) {
      const id = row.menuId ?? row.id;
      if (id == null) continue;
      attempted++;
      try {
        await apiClient.delete(`${MENU_BASE_PATH}/${encodeURIComponent(String(id))}`);
        deleted++;
      } catch (e) {
        errors.push(`${String(id)}: ${e instanceof Error ? e.message : 'failed'}`);
      }
    }
  } catch (e) {
    errors.push(`list: ${e instanceof Error ? e.message : 'failed'}`);
  }
  return { attempted, deleted, errors };
}

/** Seed BillBook module-wise menus. Two-pass: parents first (to obtain
 *  IDs the backend assigns), then children referencing the parent ID. */
export async function seedBillBookMenus(): Promise<{
  cleared: { attempted: number; deleted: number; errors: string[] };
  parents: { created: number; failed: number; errors: string[] };
  children: { created: number; failed: number; errors: string[] };
}> {
  const cleared = await deleteAllLiveMenus();

  const parents = BILLBOOK_MENU_ROWS.filter((r) => !r.parentCode);
  const children = BILLBOOK_MENU_ROWS.filter((r) => r.parentCode);

  const codeToId: Record<string, string | number> = {};

  const parentsResult = { created: 0, failed: 0, errors: [] as string[] };
  for (const m of parents) {
    try {
      const r = await apiClient.post(MENU_BASE_PATH, {
        code: m.code,
        name: m.name,
        icon: m.icon,
        sortOrder: m.sortOrder,
        isActive: m.isActive,
        url: m.path,
        path: m.path,
      });
      const pickedId = pickRowId(r.data);
      if (pickedId != null) codeToId[m.code] = pickedId;
      parentsResult.created++;
    } catch (e) {
      parentsResult.failed++;
      parentsResult.errors.push(`${m.code}: ${e instanceof Error ? e.message : 'failed'}`);
    }
  }

  const childrenResult = { created: 0, failed: 0, errors: [] as string[] };
  for (const m of children) {
    try {
      const parentId = m.parentCode ? codeToId[m.parentCode] : undefined;
      await apiClient.post(MENU_BASE_PATH, {
        code: m.code,
        name: m.name,
        parentId,
        sortOrder: m.sortOrder,
        isActive: m.isActive,
        url: m.path,
        path: m.path,
      });
      childrenResult.created++;
    } catch (e) {
      childrenResult.failed++;
      childrenResult.errors.push(`${m.code}: ${e instanceof Error ? e.message : 'failed'}`);
    }
  }

  return { cleared, parents: parentsResult, children: childrenResult };
}

function pickRowId(d: unknown): string | number | undefined {
  if (!d || typeof d !== 'object') return undefined;
  const o = d as Record<string, unknown> & { data?: unknown };
  const inner = (o.data && typeof o.data === 'object' ? (o.data as Record<string, unknown>) : o);
  const candidates = ['menuId', 'id', 'rowId'];
  for (const k of candidates) {
    const v = (inner as Record<string, unknown>)[k];
    if (typeof v === 'string' || typeof v === 'number') return v;
  }
  return undefined;
}

export const BILLBOOK_MENU_COUNT = BILLBOOK_MENU_ROWS.length;

/* -------------------------------------------------------------------- */
/*  Restaurant menu seed (G55 workaround)                               */
/* -------------------------------------------------------------------- */

export interface JaipurBackyardSeedResult {
  tenantId: string;
  tableId: number;
  qrToken: string;
  stickerUrl: string;
  anonymous: { byQr: string; menu: string; print: string };
  seeded: {
    table:      { inserted: number; existing: number };
    categories: { inserted: number; existing: number };
    items:      { inserted: number; existing: number };
  };
  durationMs?: number;
}

/**
 * Seed the canonical "Jaipur Backyard" restaurant demo into the
 * active tenant. Calls the backend's idempotent admin endpoint
 * (`POST /api/admin/migration/seed-jaipur-backyard`) which inserts
 * 5 MenuCategories + 24 MenuItems + 1 RestaurantTable with a stable
 * QR token. Existing rows (matched on Code) are left alone — the
 * action is safe to re-run.
 *
 * Used as the G55 workaround: gives every fresh tenant a populated
 * Menu Builder + working dine-in flow in one click, without
 * touching the database directly.
 */
export async function seedJaipurBackyardMenu(opts?: {
  tenantId?: string;
}): Promise<JaipurBackyardSeedResult> {
  const r = await apiClient.post('/admin/migration/seed-jaipur-backyard', opts ?? {});
  return r.data as JaipurBackyardSeedResult;
}

/* -------------------------------------------------------------------- */
/*  Restaurant nav sub-menu seed                                        */
/* -------------------------------------------------------------------- */

/**
 * Rows from `BILLBOOK_MENU_ROWS` that belong to the Restaurant module —
 * the M-REST parent + every M-REST-* child. Used by
 * `seedRestaurantNavMenus()` so we only touch this module's nav and
 * leave the rest of the sidebar alone.
 */
function pickRestaurantNavRows(): MenuSeedRow[] {
  return BILLBOOK_MENU_ROWS.filter(
    (r) => r.code === 'M-REST' || r.parentCode === 'M-REST',
  );
}

export interface RestaurantNavSeedResult {
  parents: { attempted: number; created: number; failed: number; errors: string[] };
  children: { attempted: number; created: number; failed: number; errors: string[] };
}

/**
 * Insert ONLY the Restaurant section of the nav menu (M-REST parent
 * + all M-REST-* children) into `/api/Menu`. Idempotent on `code` —
 * the backend's create endpoint should 409/skip on dup. Errors
 * surface in the result rather than throwing so the caller can
 * proceed even if half the rows already exist.
 */
export async function seedRestaurantNavMenus(): Promise<RestaurantNavSeedResult> {
  const rows = pickRestaurantNavRows();
  const parentRows = rows.filter((r) => !r.parentCode);
  const childRows  = rows.filter((r) =>  r.parentCode);

  const codeToId: Record<string, string | number> = {};
  const parents = { attempted: parentRows.length, created: 0, failed: 0, errors: [] as string[] };
  for (const m of parentRows) {
    try {
      const r = await apiClient.post(MENU_BASE_PATH, {
        code: m.code, name: m.name, icon: m.icon, sortOrder: m.sortOrder,
        isActive: m.isActive, url: m.path, path: m.path,
      });
      const id = pickRowId(r.data);
      if (id != null) codeToId[m.code] = id;
      parents.created++;
    } catch (e) {
      parents.failed++;
      parents.errors.push(`${m.code}: ${e instanceof Error ? e.message : 'failed'}`);
    }
  }

  const children = { attempted: childRows.length, created: 0, failed: 0, errors: [] as string[] };
  for (const m of childRows) {
    try {
      const parentId = m.parentCode ? codeToId[m.parentCode] : undefined;
      await apiClient.post(MENU_BASE_PATH, {
        code: m.code, name: m.name, parentId,
        sortOrder: m.sortOrder, isActive: m.isActive,
        url: m.path, path: m.path,
      });
      children.created++;
    } catch (e) {
      children.failed++;
      children.errors.push(`${m.code}: ${e instanceof Error ? e.message : 'failed'}`);
    }
  }

  return { parents, children };
}

/* -------------------------------------------------------------------- */
/*  Combined Restaurant demo seeder                                     */
/* -------------------------------------------------------------------- */

export interface RestaurantDemoSeedResult {
  /** Result of the backend Jaipur Backyard migration (dining menu). */
  jaipurBackyard: JaipurBackyardSeedResult;
  /** Result of seeding the Restaurant section of the nav sidebar. */
  navMenus: RestaurantNavSeedResult;
}

/**
 * **One-click "Restaurant demo" seeder.** Runs in this order:
 *   1. `POST /api/admin/migration/seed-jaipur-backyard` — inserts
 *      5 MenuCategories + 24 MenuItems + 1 RestaurantTable with QR
 *      (the dining menu the customer sees)
 *   2. `seedRestaurantNavMenus()` — inserts the Restaurant section
 *      of the operator-side sidebar (Floor, Orders, KOT, Kitchen
 *      Display, Menu Builder, Tables, QR Codes, etc.)
 *
 * Both steps are idempotent — safe to re-run. Returns a combined
 * report so the FE can surface "X categories + Y items + Z sub-menus
 * seeded" in one alert.
 */
export async function seedRestaurantDemo(opts?: {
  tenantId?: string;
}): Promise<RestaurantDemoSeedResult> {
  const jaipurBackyard = await seedJaipurBackyardMenu(opts);
  const navMenus       = await seedRestaurantNavMenus();
  // Drop cached sidebar so it re-fetches the new rows.
  try { localStorage.removeItem('kss-dynamic-menu'); } catch { /* SSR / no-op */ }
  return { jaipurBackyard, navMenus };
}
